import { Card, CardContent } from '@skylabs-digital/backoffice-kit';

/**
 * Las cuatro pestañas de Preferences editan cuatro filtros distintos que corren
 * en cadena antes de encolar una notificación, y sin ese contexto la pantalla no
 * se entiende: no es "configuración" suelta, es la respuesta a "por qué este
 * mensaje no salió".
 *
 * El orden y los atajos están tomados del PolicyEngine
 * (backend/src/application/pipeline/policies): CategoryBypass -> OptOut ->
 * UserPreference -> TenantPreference -> FrequencyBudget -> RateLimit.
 */

export type PreferencesTab = 'user' | 'tenant' | 'optouts' | 'budgets';

interface Explanation {
  what: string;
  key: string;
  gotcha?: string;
}

const EXPLANATIONS: Record<PreferencesTab, Explanation> = {
  user: {
    what: 'Lo que un usuario eligió recibir: por canal y por categoría. Es el "quiero recibos por mail pero no promociones por WhatsApp".',
    key: 'Clave: app + tenant + usuario + canal + categoría.',
    gotcha:
      'Solo se evalúa si el envío trae userId y tenantId. Y MARKETING funciona al revés que el resto: sin una fila que lo habilite explícitamente, se rechaza.',
  },
  tenant: {
    what: 'Lo mismo pero un nivel arriba: apaga un canal o una categoría para todo un tenant de una vez.',
    key: 'Clave: app + tenant + canal + categoría.',
    gotcha: 'TRANSACTIONAL saltea esta capa: un recibo o un cambio de clave sale igual.',
  },
  optouts: {
    what: 'La baja total de un canal. Ese usuario no recibe nada por ahí, sin importar la categoría. Es la que existe por obligación legal.',
    key: 'Clave: app + usuario + canal. No depende del tenant.',
    gotcha:
      'CRITICAL la saltea. Las bajas con motivo bounce o complaint las creó el webhook del proveedor, no una persona.',
  },
  budgets: {
    what: 'El tope semanal por categoría, para que un producto no sature a un usuario. El contador lo sube el worker cuando entrega.',
    key: 'Clave: app + categoría, y opcionalmente tenant o usuario.',
    gotcha: 'CRITICAL no consume budget ni se frena por él.',
  },
};

export function PolicyExplainer({ tab }: { tab: PreferencesTab }) {
  const e = EXPLANATIONS[tab];

  return (
    <Card>
      <CardContent className="space-y-2 pt-6 text-sm">
        <p className="text-foreground">{e.what}</p>
        <p className="text-xs text-muted-foreground">{e.key}</p>
        {e.gotcha ? <p className="text-xs text-muted-foreground">{e.gotcha}</p> : null}
      </CardContent>
    </Card>
  );
}

/**
 * Cabecera de la pantalla: el orden de la cadena. Se muestra una sola vez,
 * arriba de las pestañas, porque es lo que explica por qué son cuatro cosas
 * distintas y no una.
 */
export function PolicyChainSummary() {
  const steps = [
    { name: 'Opt-out', detail: 'el usuario se dio de baja del canal' },
    { name: 'Preferencia del usuario', detail: 'lo que eligió por categoría' },
    { name: 'Preferencia del tenant', detail: 'lo que el tenant apagó' },
    { name: 'Budget', detail: 'tope semanal por categoría' },
    { name: 'Rate limit', detail: 'techo por app y canal' },
  ];

  return (
    <Card>
      <CardContent className="space-y-3 pt-6">
        <p className="text-sm text-foreground">
          Antes de encolar cualquier notificación, noten la pasa por estos filtros en orden. El
          primero que rechaza corta, y queda registrado como{' '}
          <code className="rounded bg-muted px-1 py-0.5 text-xs">notification.policy.rejected</code>
          .
        </p>
        <ol className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
          {steps.map((s, i) => (
            <li key={s.name} className="flex items-center gap-2">
              <span>
                <span className="font-medium text-foreground">{s.name}</span> · {s.detail}
              </span>
              {i < steps.length - 1 ? <span aria-hidden>→</span> : null}
            </li>
          ))}
        </ol>
        <p className="text-xs text-muted-foreground">
          Dos categorías tienen atajos: <strong>CRITICAL</strong> saltea todo, incluidos opt-outs y
          budgets, y <strong>TRANSACTIONAL</strong> saltea las preferencias de tenant.
        </p>
      </CardContent>
    </Card>
  );
}
